import {
  useMemo,
  forwardRef,
  type ReactElement,
} from 'react';
import {
  FlagImage,
  CountryData,
  CountryIso2,
  parseCountry,
  usePhoneInput,
  defaultCountries,
} from 'react-international-phone';
import 'react-international-phone/style.css';
import {
  FormLabel,
  FormControl,
  FormHelperText,
  Input as JoyInput,
  InputProps as JoyInputProps,
} from '@mui/joy';

export type SupportedCountries = Omit<
  CountryIso2,
  'us'
>;
export interface PhoneInputProps
  extends Omit<JoyInputProps, 'error' | 'value' | 'onChange'> {
  label?: string;
  error?: string;
  value?: string;
  startDecorator?: ReactElement;
  onChange?: (phone: Phone) => void;
};

/**
 * This component might trigger the warning: "A FormControl can contain only one control component." 
 * As detailed in issue #37764 on the MUI Joy UI GitHub repository, this warning is acknowledged and 
 * is currently classified as an enhancement for future updates, rather than a critical bug. It can be safely ignored. 
 * 
 * For more details, refer to the issue: [GitHub Issue #37764](https://github.com/mui/material-ui/issues/37764).
 */

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(({
  onChange,
  onBlur,
  label,
  name,
  value,
  endDecorator,
  error,
  color = 'neutral',
  disabled,
  ...props
}: PhoneInputProps, ref) => {
  const defaultCountry = 'us' as const;

  const countries: CountryData[] = useMemo(() =>
    defaultCountries.filter(country => parseCountry(country).iso2 === defaultCountry),
    []
  );

  const { inputValue, handlePhoneValueChange, inputRef } = usePhoneInput({
    defaultCountry,
    value,
    countries,
    onChange: ({ phone }) => {
      onChange?.(phone);
    },
  });

  const handleStrictPhoneValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const US_PREFIX = '+1';
    const input = e.target.value;

    if (!input.startsWith(US_PREFIX)) {
      if (inputRef.current) {
        inputRef.current.value = US_PREFIX;
      }
      return;
    }

    handlePhoneValueChange(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const cursorPosition = input.selectionStart;
    if ((e.key === 'Backspace' && cursorPosition! <= 2) ||
      (e.key === 'Delete' && cursorPosition! < 2)) {
      e.preventDefault();
    }
  };

  return (
    <FormControl
      disabled={disabled}
      error={!!error}
      sx={{ minHeight: 90 }}>
      {label && (
        <FormLabel sx={{ marginBottom: 0.5 }}>
          {label}
        </FormLabel>
      )}
      <JoyInput
        name={name}
        value={inputValue}
        error={!!error}
        color={color}
        disabled={disabled}
        onBlur={onBlur}
        onChange={handleStrictPhoneValueChange}
        onKeyDown={handleKeyDown}
        slotProps={{
          input: {
            id: name,
            ref: ref = inputRef,
          },
        }}
        startDecorator={<FlagImage iso2={defaultCountry} />}
        endDecorator={endDecorator}
        {...props}
      />
      {(error) && (
        <FormHelperText sx={{ marginTop: 0.5 }}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
});